import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LucideHome, LucideFactory } from 'lucide-react';

const PATHS = [
  {
    id: 'home-automation',
    title: 'Home Automation',
    subtitle: 'Residential Solutions',
    icon: <LucideHome size={32} />,
    desc: 'Transform your residence into a sanctuary of comfort and intelligence.',
    to: '/home-automation',
    img: 'https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&q=80&w=1200',
  },
  {
    id: 'industrial-automation',
    title: 'Industrial Automation',
    subtitle: 'Enterprise Solutions',
    icon: <LucideFactory size={32} />,
    desc: 'Scale operations with high-fidelity automation and mission-critical reliability.',
    to: '/industrial-automation',
    img: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=1200',
  },
];

export default function InteractiveUI() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // ── LAYER 1: Far Background Parallax ──
  const bgY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1]);

  // ── UI Card Reveal ──
  const cardY = useTransform(scrollYProgress, [0, 0.4], [100, 0]);
  const cardOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);

  return (
    <section ref={containerRef} className="relative min-h-screen flex items-center justify-center bg-theme py-24">
      <motion.div 
        className="max-w-7xl w-full px-8 md:px-12"
        style={{ y: cardY, opacity: cardOpacity }}
      >
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light mb-4 text-theme-primary">Choose Your Path</h2>
          <div className="w-20 h-0.5 bg-iotiq-green mx-auto opacity-20" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 h-[70vh] min-h-[500px]">
          {PATHS.map((path) => (
            <motion.div 
              key={path.id}
              whileHover={{ scale: 0.98 }}
              className="relative rounded-[3rem] overflow-hidden group cursor-pointer shadow-2xl"
            >
              <img 
                src={path.img} 
                alt={path.title} 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
              />
              {/* Full-card blur overlay — invisible by default, fades in on hover */}
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

              {/* Content Pane — always visible at bottom, sits above blur overlay */}
              <div className="absolute inset-x-0 bottom-0 p-8 flex flex-col justify-end">
                <div className="w-16 h-16 rounded-3xl bg-white/10 backdrop-blur-md flex items-center justify-center mb-6 text-white border border-white/20 shadow-lg group-hover:scale-110 transition-transform duration-500">
                  {path.icon}
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.5em] text-gold mb-2 block drop-shadow-md">
                  {path.subtitle}
                </span>
                <h3 className="text-3xl md:text-4xl font-serif text-white mb-4 drop-shadow-md">{path.title}</h3>
                <p className="text-white/90 mb-8 max-w-sm font-light leading-relaxed drop-shadow-sm">{path.desc}</p>
                <Link to={path.to} className="w-fit px-8 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full text-[10px] uppercase tracking-ultra font-bold shadow-lg hover:bg-gold hover:text-black transition-all duration-500">
                  Explore Path
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
